import React from 'react';
import {
  Card,
  CardContent,
  Stack,
  TextField,
  MenuItem,
  Button,
  Box,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Refresh as RefreshIcon, FilterAltOff as ClearIcon } from '@mui/icons-material';
import useTeamOptions from '../../hooks/useTeamOptions';

export const TIME_FRAMES = [
  { value: 'week', label: 'Last week' },
  { value: 'month', label: 'Last month' },
  { value: 'quarter', label: 'Last quarter' },
  { value: 'year', label: 'Last year' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

/**
 * The shared filter bar used by Dashboard, Analytics and Tickets.
 *
 * Previously each page hand-rolled its own copy of these controls; this is the
 * single implementation. Extra page-specific controls can be passed as
 * `children` and render inline with the standard ones.
 */
const FilterBar = ({
  filterOptions,
  setFilterOptions,
  onApply,
  onClear,
  loading = false,
  children,
  showTeam = true,
}) => {
  const isCustom = filterOptions.timeFrame === 'custom';

  // Team options come from the server's live squad vocabulary — never a
  // hardcoded client list. See hooks/useTeamOptions.
  const teamOptions = useTeamOptions(showTeam);

  const handleTeam = (event) => {
    const squad = event.target.value;
    setFilterOptions((prev) => ({ ...prev, squad }));
    if (onApply) onApply({ squad });
  };

  const handleTimeFrame = (event) => {
    const timeFrame = event.target.value;
    setFilterOptions((prev) => ({
      ...prev,
      timeFrame,
      startDate: timeFrame === 'custom' ? prev.startDate : null,
      endDate: timeFrame === 'custom' ? prev.endDate : null,
    }));
    if (timeFrame !== 'custom' && onApply) onApply({ timeFrame });
  };

  const activeCount = [
    filterOptions.squad,
    filterOptions.assignee,
    filterOptions.category,
    filterOptions.parentEpic,
  ].filter(Boolean).length;

  return (
    <Card>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <TextField
            select
            size="small"
            label="Time frame"
            value={filterOptions.timeFrame || 'month'}
            onChange={handleTimeFrame}
            sx={{ minWidth: 168 }}
          >
            {TIME_FRAMES.map((tf) => (
              <MenuItem key={tf.value} value={tf.value}>
                {tf.label}
              </MenuItem>
            ))}
          </TextField>

          {showTeam && (
            <TextField
              select
              size="small"
              label="Team"
              value={filterOptions.squad || ''}
              onChange={handleTeam}
              sx={{ minWidth: 168 }}
            >
              <MenuItem value="">All teams</MenuItem>
              {teamOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {isCustom && (
            <>
              <DatePicker
                label="From"
                value={filterOptions.startDate}
                onChange={(d) =>
                  setFilterOptions((prev) => ({ ...prev, startDate: d, timeFrame: 'custom' }))
                }
                slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
              />
              <DatePicker
                label="To"
                value={filterOptions.endDate}
                onChange={(d) =>
                  setFilterOptions((prev) => ({ ...prev, endDate: d, timeFrame: 'custom' }))
                }
                slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
              />
            </>
          )}

          {children}

          <Box sx={{ flexGrow: 1 }} />

          {activeCount > 0 && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${activeCount} filter${activeCount > 1 ? 's' : ''}`}
            />
          )}

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => onApply && onApply(filterOptions)}
              disabled={loading}
            >
              Apply
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<ClearIcon />}
              onClick={onClear}
              disabled={loading}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default FilterBar;
